import { z } from "zod";
import { FactNodeOrigin } from "./FactNode.js";
import { IdentityEmbedSchema } from "./Identity.js";
import { noteTitleMaxLength } from "./NoteV2.js";
import {
  PaginationMetaResponseSchema,
  PaginationQuerySchema,
} from "./Pagination.js";

export enum NoteProvenanceSource {
  BuildBlock = "build-block",
  GlobalIdea = "global-idea",
  Task = "task",
}

const NoteProvenanceSchema = z.object({
  source: z.enum(NoteProvenanceSource),
  /**
   * @deprecated Use blockId on the Note model instead.
   */
  blockPath: z.string().optional(),
});

export const noteContentMaxLength = 25000;

export const NoteContentSchema = z.object({
  provenance: NoteProvenanceSchema,
  title: z.string().optional(),
  /**
   * @deprecated Use tagIds array on the Note model instead.
   */
  bucket: z.string().optional(),
  plainText: z.string(),
});

export const NoteSchema = z.object({
  id: z.uuid(),
  private: z.boolean().default(false),
  creatorId: z.uuid(),
  organizationId: z.uuid(),
  content: NoteContentSchema,
  createdAt: z.date(),
  updatedAt: z.date().nullable(),
  removedAt: z.date().nullable(),
  createdByAssistant: z.boolean(),
  createdByApiKeyId: z.uuid().nullish(),
  createdByOAuthClientId: z.string().nullish(),
  createdByConnector: z.union([z.enum(FactNodeOrigin), z.string()]).nullable(),
  creator: IdentityEmbedSchema,
  externalId: z.string().nullable(),
  tagIds: z.array(z.uuid()),
  taskId: z.string().nullable(),
  blockId: z.uuid().nullable(),
});

export const FilterNoteParamsSchema = z.object({
  source: z.enum(NoteProvenanceSource).optional(),
  blockId: z.uuid().optional(),
  taskId: z.uuid().optional().nullable(),
  /**
   * @deprecated Use tagIds for filtering. Filter by tag IDs. If multiple IDs provided, returns convos that have ANY of the tags.
   */
  inBucket: z.string().optional(),
  /**
   * Filter by tag IDs. If multiple IDs provided, returns notes that have ANY of the tags.
   */
  tagIds: z.array(z.uuid()).optional(),
  skip: z.int().optional(),
  take: z.int().max(100).optional(),
});

export type Note = z.infer<typeof NoteSchema>;
export type NoteContent = z.infer<typeof NoteContentSchema>;
export type FilterNoteParams = z.infer<typeof FilterNoteParamsSchema>;

export const CreateNoteToolInputSchema = z.object({
  title: z
    .string()
    .min(1)
    .max(noteTitleMaxLength)
    .optional()
    .describe("Generated from the content when omitted."),
  content: z.string().min(1).max(noteContentMaxLength),
  private: z
    .boolean()
    .optional()
    .describe("Only the creator can see a private note. Defaults to false."),
});
export const NoteToolOutputSchema = z.object({
  id: NoteSchema.shape.id,
  title: z.string().nullable(),
  content: z.string(),
  private: z.boolean(),
  createdAt: z.string(),
});
export const CreateNoteToolOutputSchema = NoteToolOutputSchema.omit({
  content: true,
});
export const ListNotesToolInputSchema = z.object({
  limit: z.number().int().min(1).max(100).default(50),
  offset: z.number().int().min(0).optional(),
});
export const ListNotesToolOutputSchema = z.object({
  notes: z.array(NoteToolOutputSchema),
});
export type CreateNoteToolInput = z.infer<typeof CreateNoteToolInputSchema>;
export type NoteToolOutput = z.infer<typeof NoteToolOutputSchema>;
export type CreateNoteToolOutput = z.infer<typeof CreateNoteToolOutputSchema>;

export function toNoteToolOutput(note: {
  id: string;
  private: boolean;
  createdAt: Date;
  content: { title?: string | null; plainText?: string | null };
}): NoteToolOutput {
  return {
    id: note.id,
    title: note.content.title ?? null,
    content: note.content.plainText ?? "",
    private: note.private,
    createdAt: note.createdAt.toISOString(),
  };
}

export function toCreateNoteToolOutput(
  note: Parameters<typeof toNoteToolOutput>[0],
): CreateNoteToolOutput {
  const { content: _content, ...output } = toNoteToolOutput(note);
  return output;
}

/**
 * ========================================
 * PUBLIC API SCHEMAS
 * ========================================
 */
export const PostNoteRequestSchema = z.object({
  externalId: NoteSchema.shape.externalId.optional(),
  title: NoteContentSchema.shape.title.optional(),
  content: NoteContentSchema.shape.plainText.max(noteContentMaxLength),
  taskId: NoteSchema.shape.taskId.optional(),
});
export const NoteListItemSchema = z.object({
  id: NoteSchema.shape.id,
  title: NoteContentSchema.shape.title.nullable(),
  content: NoteContentSchema.shape.plainText,
  createdAt: NoteSchema.shape.createdAt,
  taskId: NoteSchema.shape.taskId,
  externalId: NoteSchema.shape.externalId,
});
export const PostNoteResponseSchema = z.object({
  note: NoteListItemSchema,
});
export const GetNotesQuerySchema = z.object({
  ...PaginationQuerySchema.shape,
});
export const GetNotesResponseSchema = z.object({
  ...PaginationMetaResponseSchema.shape,
  items: z.array(NoteListItemSchema),
});

export type PostNoteRequest = z.infer<typeof PostNoteRequestSchema>;
export type PostNoteResponse = z.infer<typeof PostNoteResponseSchema>;
export type NoteListItem = z.infer<typeof NoteListItemSchema>;
export type GetNotesQuery = z.infer<typeof GetNotesQuerySchema>;
export type GetNotesResponse = z.infer<typeof GetNotesResponseSchema>;
/**
 * ========================================
 * END PUBLIC API SCHEMAS
 * ========================================
 */
